import { html } from "lit";
import type { RouteConfig, BaseRouteConfig } from "@lit-labs/router";
import { pb } from "./api/client.js";
import { isOperator } from "./api/auth.js";
import { navigate } from "./nav.js";

import "./pages/page-login.js";
import "./pages/page-onboarding.js";
import "./pages/page-security.js";
import "./pages/page-account.js";
import "./pages/page-invite.js";
import "./pages/page-workspaces.js";
import "./pages/page-workspace.js";
import "./pages/page-repository.js";
import "./pages/page-data.js";
import "./pages/page-deployments.js";
import "./pages/page-deployment.js";
import "./pages/page-edition.js";
import "./pages/page-calendars.js";
import "./pages/page-build.js";
import "./pages/page-teams.js";
import "./pages/page-team.js";
import "./pages/page-api-keys.js";
import "./pages/page-usage.js";
import "./pages/page-admin.js";
import "./pages/page-admin-teams.js";

// ===========================================================================
// PAGE ELEMENT REGISTRATION
// ---------------------------------------------------------------------------
// Each page is a self-registering custom element (it calls customElements.define
// in its own module). Pages are built in parallel; as each one lands, add its
// side-effect import here so the element is defined before its route renders:
//
//   import "./pages/page-login.js";
//   import "./pages/page-workspaces.js";
//   import "./pages/page-workspace.js";
//   import "./pages/page-build.js";
//   import "./pages/page-teams.js";
//   import "./pages/page-team.js";
//   import "./pages/page-api-keys.js";
//   import "./pages/page-usage.js";
//   import "./pages/page-not-found.js";
//
// Until a page's import is added, its route still resolves — an undefined custom
// element simply renders empty, so the app never hard-fails on a missing page.
// A route for every planned page already exists in `routes` below; if you add a
// NEW page/route, push a RouteConfig entry onto `routes` (it is a mutable export)
// or add a literal here, and add the matching import above.
// ===========================================================================

/**
 * guard is the auth gate. Anonymous users hitting an authenticated route are
 * redirected to /login?next=<attempted path>. Returning false cancels the
 * in-progress navigation (the redirect drives the new render). Opt a route in
 * with `enter: guard`.
 */
export function guard(): boolean {
  if (pb.authStore.isValid) return true;
  navigate("/login?next=" + encodeURIComponent(location.pathname + location.search), {
    replace: true,
  });
  return false;
}

/**
 * adminGuard gates the operator-only admin pages. Anonymous users get the
 * normal /login bounce (via guard); signed-in non-operators are sent home —
 * the Admin nav item is hidden for them, so landing here means a typed or
 * stale URL, not a flow to explain. The server enforces the flag on every
 * /v1/admin/* call regardless; this only spares non-operators an error page.
 */
export function adminGuard(): boolean {
  if (!guard()) return false;
  if (isOperator()) return true;
  navigate("/", { replace: true });
  return false;
}

// The route table. @lit-labs/router matches the full pathname via URLPattern, so
// order is not significant; specific paths precede parameterized ones for
// readability. Every entry maps to a <page-*> custom element registered above.
export const routes: RouteConfig[] = [
  {
    path: "/",
    enter: guard,
    render: () => html`<page-workspaces></page-workspaces>`,
  },
  {
    path: "/login",
    render: () => html`<page-login></page-login>`,
  },
  {
    // First-login onboarding (display name + optional passkey). Guarded: it
    // mutates the signed-in user; page-login routes fresh accounts here.
    path: "/welcome",
    enter: guard,
    render: () => html`<page-onboarding></page-onboarding>`,
  },
  {
    // Team invitation landing page. Deliberately UNGUARDED: the link arrives by
    // email and the recipient may have no account yet — the page looks the
    // invite up anonymously and sends them to /login?next=… itself.
    path: "/invite",
    render: () => html`<page-invite></page-invite>`,
  },
  {
    path: "/security",
    enter: guard,
    render: () => html`<page-security></page-security>`,
  },
  {
    // Data export and account deletion. The routes behind it are optional on
    // the server; the page probes them itself and shows only a notice when
    // they are off.
    path: "/account",
    enter: guard,
    render: () => html`<page-account></page-account>`,
  },
  {
    path: "/workspaces",
    enter: guard,
    render: () => html`<page-workspaces></page-workspaces>`,
  },
  {
    path: "/workspaces/:id",
    enter: guard,
    render: ({ id }) => html`<page-workspace .workspaceId=${id ?? ""}></page-workspace>`,
  },
  {
    // Linked from a branch workspace's header. The routes behind it exist only
    // while the server has repositories on; without them the page shows its
    // load error.
    path: "/repositories/:id",
    enter: guard,
    render: ({ id }) => html`<page-repository .repositoryId=${id ?? ""}></page-repository>`,
  },
  {
    // The repository's data drops and seals. The routes behind it exist only
    // while the server has the data area on; the page probes them itself and
    // shows only a notice when they are off.
    path: "/repositories/:id/data",
    enter: guard,
    render: ({ id }) => html`<page-data .repositoryId=${id ?? ""}></page-data>`,
  },
  {
    // The repository's deployments, one deployment, and one edition. The
    // routes behind them exist only while the server has deployments on;
    // the list page probes them itself and shows only a notice when off.
    path: "/repositories/:id/deployments",
    enter: guard,
    render: ({ id }) => html`<page-deployments .repositoryId=${id ?? ""}></page-deployments>`,
  },
  {
    path: "/repositories/:id/deployments/:deploymentId",
    enter: guard,
    render: ({ id, deploymentId }) =>
      html`<page-deployment
        .repositoryId=${id ?? ""}
        .deploymentId=${deploymentId ?? ""}
      ></page-deployment>`,
  },
  {
    path: "/repositories/:id/deployments/:deploymentId/editions/:editionId",
    enter: guard,
    render: ({ id, deploymentId, editionId }) =>
      html`<page-edition
        .repositoryId=${id ?? ""}
        .deploymentId=${deploymentId ?? ""}
        .editionId=${editionId ?? ""}
      ></page-edition>`,
  },
  {
    path: "/builds/:id",
    enter: guard,
    render: ({ id }) => html`<page-build .buildId=${id ?? ""}></page-build>`,
  },
  {
    path: "/teams",
    enter: guard,
    render: () => html`<page-teams></page-teams>`,
  },
  {
    path: "/teams/:id",
    enter: guard,
    render: ({ id }) => html`<page-team .teamId=${id ?? ""}></page-team>`,
  },
  {
    // The team's workday calendars, which exist only with deployments on.
    path: "/teams/:id/calendars",
    enter: guard,
    render: ({ id }) => html`<page-calendars .teamId=${id ?? ""}></page-calendars>`,
  },
  {
    path: "/api-keys",
    enter: guard,
    render: () => html`<page-api-keys></page-api-keys>`,
  },
  {
    path: "/usage",
    enter: guard,
    render: () => html`<page-usage></page-usage>`,
  },
  {
    path: "/admin",
    enter: adminGuard,
    render: () => html`<page-admin></page-admin>`,
  },
  {
    path: "/admin/teams",
    enter: adminGuard,
    render: () => html`<page-admin-teams></page-admin-teams>`,
  },
];

// fallback (unmatched paths). Rendered with the component kit so it works before
// a dedicated page-not-found element is registered; swap in <page-not-found>
// once it lands.
export const fallback: BaseRouteConfig = {
  render: () => html`
    <bn-empty-state
      icon="∅"
      heading="Page not found"
      message="The page you are looking for does not exist."
    >
      <bn-button slot="actions" variant="primary" @click=${() => navigate("/")}>
        Go home
      </bn-button>
    </bn-empty-state>
  `,
};
